import { z } from 'zod'

/**
 * Insights (CLI-959): the short list of key coverage at the top of a report's email, one line per
 * article — legacy's `<li><b>Outlet</b>: <a href>Title</a></li>`. Which lines an issue starts with
 * is the report's `settings.insights.mode`; where and how they show is the template's, with a
 * default that mirrors legacy's email (after the intro, a plain list, no heading).
 *
 * - `off`: none.
 * - `key-mentions`: legacy's default — articles from the header's highlighted topics first (the
 *   topics a highlight colour marks as key, CLI-964), up to two per header from the first three
 *   headers that have any, then two per header from the others, to a cap of six (two per header
 *   when three or fewer headers have news). Legacy picked those headers at random; this picks
 *   them in report order, so a re-pull does not reshuffle the list (Kaushik, 2026-10-22).
 * - `by-header`: legacy's `contentByHeader`: every article, under its header's name, the
 *   highlighted topics' first.
 * - `by-hand`: legacy's `isManualEmailInsightsEnabled`: the list starts empty for the associate.
 */
export const INSIGHTS_MODES = ['off', 'key-mentions', 'by-header', 'by-hand'] as const
export const insightsModeSchema = z.enum(INSIGHTS_MODES)
export type InsightsMode = z.infer<typeof insightsModeSchema>

/** What the report settings show for each mode. */
export const INSIGHTS_MODE_LABELS: Record<InsightsMode, string> = {
  off: 'Off',
  'key-mentions': 'Key mentions (up to 6)',
  'by-header': 'Every article, by header',
  'by-hand': 'Written by hand',
}

/** Legacy's cap on a line's title (insights-util.js `article_title`). */
export const INSIGHT_TITLE_MAX = 280

/**
 * One line. Generated from an article (`itemId`) or written by hand (`itemId` null): the outlet
 * is the bold lead, the title the words, the link optional. `group` is the header name a
 * `by-header` line sits under.
 */
export const issueInsightSchema = z.object({
  id: z.string().min(1).max(64),
  itemId: z.uuid().nullable(),
  outlet: z.string().max(200).nullable(),
  title: z.string().min(1).max(1000),
  url: z.url().max(2000).nullable(),
  group: z.string().max(200).nullable(),
})
export type IssueInsight = z.infer<typeof issueInsightSchema>

/**
 * What an issue stores once someone edits its list; absent, the list is generated. Also the body
 * of `PUT /api/report-issues/:id/insights`: the whole list, in order.
 */
export const storedInsightsSchema = z.object({ entries: z.array(issueInsightSchema).max(300) })
export type StoredInsights = z.infer<typeof storedInsightsSchema>

/**
 * `POST /api/report-issues/:id/insights/lines`: one article of the issue, added to the list as the
 * generator would have written its line — legacy's "Add Clips To Insight".
 */
export const addInsightLineSchema = z.object({ itemId: z.uuid() })
export type AddInsightLine = z.infer<typeof addInsightLineSchema>

/** `GET /api/report-issues/:id/insights`. */
export const issueInsightsResponseSchema = z.object({
  mode: insightsModeSchema,
  /** True once the list was edited by hand; false while it is generated from the articles. */
  edited: z.boolean(),
  entries: z.array(issueInsightSchema),
})
export type IssueInsightsResponse = z.infer<typeof issueInsightsResponseSchema>
